import React, { useEffect, useState, useCallback } from "react";
import { TrendingUp, Briefcase, CalendarClock, Users } from "lucide-react";
import { brokerPnl, brokerMoney, getReminders, teamGet, wallTicker } from "../lib/api";

const usd = (v, d = 0) => `${v < 0 ? "-" : ""}$${Math.abs(Number(v || 0)).toLocaleString(undefined, { maximumFractionDigits: d, minimumFractionDigits: d })}`;
const tone = (v) => (v > 0 ? "text-emerald-300" : v < 0 ? "text-rose-300" : "text-cyan-100");

const Panel = ({ icon: Icon, title, children, id }) => (
  <div className="nx-glass rounded-2xl p-5 flex flex-col min-h-0 overflow-hidden" data-testid={`wall-panel-${id}`}>
    <p className="nx-mono text-xs tracking-[0.3em] text-cyan-300/60 flex items-center gap-2 mb-3"><Icon className="w-4 h-4" /> {title}</p>
    <div className="flex-1 min-h-0 overflow-hidden">{children}</div>
  </div>
);

export const WallTicker = ({ userId }) => {
  const [items, setItems] = useState([]);
  useEffect(() => {
    const load = () => wallTicker(userId).then((d) => setItems(d.items || [])).catch(() => {});
    load(); const t = setInterval(load, 60000); return () => clearInterval(t);
  }, [userId]);
  if (!items.length) return null;
  const row = items.map((i) => (
    <span key={i.sym} className="mx-8 nx-mono text-lg whitespace-nowrap">
      <b className="text-cyan-50">{i.sym}</b> <span className="text-cyan-100/80">{Number(i.price).toLocaleString()}</span>{" "}
      <span className={tone(i.chg)}>{i.chg > 0 ? "▲" : i.chg < 0 ? "▼" : "•"} {Math.abs(i.chg).toFixed(2)}%</span>
    </span>
  ));
  return (
    <div className="absolute bottom-0 left-0 right-0 h-12 z-30 bg-[#030712]/90 border-t border-cyan-300/15 overflow-hidden flex items-center" data-testid="wall-ticker">
      <div className="flex nx-ticker">{row}{row}</div>
      <style>{`@keyframes nxTick { from { transform: translateX(0) } to { transform: translateX(-50%) } } .nx-ticker { animation: nxTick 60s linear infinite; }`}</style>
    </div>
  );
};

export const WallPanels = ({ userId, onPnl }) => {
  const [pnl, setPnl] = useState(null);
  const [money, setMoney] = useState(null);
  const [rem, setRem] = useState([]);
  const [team, setTeam] = useState(null);
  const load = useCallback(async () => {
    const [p, m, r, t] = await Promise.all([
      brokerPnl(userId).catch(() => null), brokerMoney(userId).catch(() => null),
      getReminders(userId).catch(() => []), teamGet(userId).catch(() => null),
    ]);
    setPnl(p); setMoney(m); setRem(r || []); setTeam(t);
    if (p && onPnl) onPnl((p.day?.realized || 0) + (p.open?.unrealized || 0));
  }, [userId, onPnl]);
  useEffect(() => { load(); const t = setInterval(load, 120000); return () => clearInterval(t); }, [load]);

  const today = (pnl?.day?.realized || 0) + (pnl?.open?.unrealized || 0);
  const open = (money?.ledger || []).filter((r) => r.status === "open" && r.venue !== "kalshi");
  return (
    <div className="grid grid-cols-2 grid-rows-2 gap-4 h-full" data-testid="wall-panels">
      <Panel icon={TrendingUp} title="TODAY" id="pnl">
        <p className={`nx-orbitron text-5xl font-black ${tone(today)}`} data-testid="wall-today-pnl">{today >= 0 ? "+" : ""}{usd(today, 2)}</p>
        <p className="text-cyan-200/60 mt-2 text-lg">Account <b className="text-cyan-50">{usd(money?.equity)}</b> · cash {usd(money?.cash?.total)}</p>
        <div className="grid grid-cols-3 gap-2 mt-4 text-center">
          {["week", "month", "all"].map((k) => (
            <div key={k} className="bg-white/5 rounded-xl py-2">
              <p className="nx-mono text-[10px] text-cyan-300/50 uppercase">{k === "all" ? "all-time" : k}</p>
              <p className={`text-xl font-bold ${tone(pnl?.[k]?.realized)}`}>{usd(pnl?.[k]?.realized)}</p>
            </div>
          ))}
        </div>
      </Panel>
      <Panel icon={Briefcase} title={`OPEN TRADES · ${open.length}`} id="trades">
        <div className="space-y-1.5">
          {open.slice(0, 7).map((r) => (
            <div key={r.id} className="flex items-center justify-between text-lg">
              <span className="font-mono text-cyan-50 truncate">{r.symbol}{r.side === "short" ? " ↓" : ""}</span>
              <span className="text-cyan-200/60">{usd(r.usd_in)}</span>
              <span className={`font-bold ${tone(r.net_pnl)}`}>{r.net_pnl >= 0 ? "+" : ""}{usd(r.net_pnl, 2)}</span>
            </div>
          ))}
          {open.length === 0 && <p className="text-cyan-200/40 text-lg">No open trades, cash is safe.</p>}
        </div>
      </Panel>
      <Panel icon={CalendarClock} title="REMINDERS & DEADLINES" id="reminders">
        <div className="space-y-2">
          {rem.slice(0, 6).map((r, i) => (
            <p key={r.id || i} className="text-lg text-cyan-50 truncate">• {r.text || r.title || r.message}</p>
          ))}
          {rem.length === 0 && <p className="text-cyan-200/40 text-lg">Nothing due. Clear runway.</p>}
        </div>
      </Panel>
      <Panel icon={Users} title="THE TEAM" id="team">
        <div className="grid grid-cols-2 gap-x-4 gap-y-1.5">
          {(team?.roster || []).slice(0, 8).map((a) => (
            <p key={a.seat} className="text-base flex justify-between">
              <span className="text-cyan-50 truncate">{a.agent_of_week ? "★ " : ""}{a.name}</span>
              <b className={a.grade_30d === "A" || a.grade_30d === "B" ? "text-emerald-300" : a.grade_30d === "D" || a.grade_30d === "F" ? "text-rose-300" : "text-cyan-200/60"}>{a.grade_30d || "—"}</b>
            </p>
          ))}
        </div>
      </Panel>
    </div>
  );
};
